// =============================================================================
//  MOTOR DA JORNADA NO TEMPO — "A Linha do Tempo do seu Coração"
//  "O Coração de Todas as Coisas" — por Magan
//  A tese do livro: todo estado é transitório. Aqui as estações mudam à vista.
//
//  Uso:  import { gerarJornada } from './jornadaEngine';
//        const j = gerarJornada(historico);   // historico = registros do /evolucao/{email}
//  Cada registro tem: score_peregrino/guardiao/amante/alquimista (0..40),
//  arquetipo_dominante, is_coracao_inteiro, created_at (ISO).
// =============================================================================
const ROOTS = ['peregrino','guardiao','amante','alquimista'];
const ROOT_NOME = { peregrino:'Peregrino', guardiao:'Guardião', amante:'Amante', alquimista:'Alquimista' };
const ROOT_ESTACAO = { peregrino:'Movimento', guardiao:'Corpo', amante:'Vínculo', alquimista:'Sentido' };
const NOMES = { peregrino:'Peregrino', guardiao:'Guardião', amante:'Amante', alquimista:'Alquimista',
  visionario:'Visionário', encontro:'Peregrino do Encontro', guerreiro:'Guerreiro', curador:'Curador',
  guardiao_sabio:'Guardião Sábio', poeta:'Poeta', profeta:'Profeta', construtor:'Construtor',
  companheiro:'Companheiro de Jornada', sabio_compassivo:'Sábio Compassivo', coracao_inteiro:'Coração Inteiro' };
const MES = ['jan','fev','mar','abr','mai','jun','jul','ago','set','out','nov','dez'];
const HOT = 60, COLD = 40;

const fmtData = (iso) => { const d = new Date(iso); return isNaN(d) ? '' : `${MES[d.getMonth()]}/${String(d.getFullYear()).slice(2)}`; };
const mean = (a) => a.reduce((s,x)=>s+x,0)/(a.length||1);
const lista = (arr) => arr.length<=1 ? (arr[0]||'') : arr.slice(0,-1).join(', ')+' e '+arr[arr.length-1];

export function gerarJornada(historico, opts = {}) {
  if (!Array.isArray(historico) || historico.length < 2) return { suficiente: false };
  // ordenar do mais antigo ao mais recente
  const hist = [...historico].sort((x,y)=> new Date(x.created_at) - new Date(y.created_at));

  const pontos = hist.map(r => {
    const pcts = {}; ROOTS.forEach(k => { pcts[k] = Math.round(((r['score_'+k]||0)/40)*100); });
    const domKey = r.arquetipo_dominante || 'peregrino';
    return { data: r.created_at, label: fmtData(r.created_at), pcts,
      dominanteKey: domKey, dominanteNome: NOMES[domKey] || domKey, isInteiro: !!r.is_coracao_inteiro };
  });

  // estação-morada: maior média ao longo do tempo
  const medias = ROOTS.map(k => ({ key:k, m: mean(pontos.map(p=>p.pcts[k])) })).sort((a,b)=>b.m-a.m);
  const morada = { key: medias[0].key, nome: ROOT_NOME[medias[0].key], estacao: ROOT_ESTACAO[medias[0].key], media: Math.round(medias[0].m) };
  // mais volátil: maior amplitude
  const amplit = ROOTS.map(k => { const vs = pontos.map(p=>p.pcts[k]); return { key:k, amp: Math.max(...vs)-Math.min(...vs) }; }).sort((a,b)=>b.amp-a.amp);
  const volatil = { key: amplit[0].key, nome: ROOT_NOME[amplit[0].key], estacao: ROOT_ESTACAO[amplit[0].key], amplitude: amplit[0].amp };

  // despertaram / silenciaram (primeiro -> último ponto)
  const ini = pontos[0].pcts, fim = pontos[pontos.length-1].pcts;
  const despertaram = ROOTS.filter(k => ini[k] < HOT && fim[k] >= HOT).map(k=>({key:k,nome:ROOT_NOME[k],estacao:ROOT_ESTACAO[k]}));
  const silenciaram = ROOTS.filter(k => ini[k] >= HOT && fim[k] < HOT).map(k=>({key:k,nome:ROOT_NOME[k],estacao:ROOT_ESTACAO[k]}));

  // mudança de estação dominante
  const domIni = pontos[0].dominanteKey, domFim = pontos[pontos.length-1].dominanteKey;
  const dominanteMudou = domIni !== domFim;
  const dominantes = [...new Set(pontos.map(p=>p.dominanteKey))];

  // período
  const dias = Math.round((new Date(pontos[pontos.length-1].data) - new Date(pontos[0].data)) / 86400000);
  const meses = Math.max(1, Math.round(dias/30));
  const periodoTexto = meses < 2 ? `${dias} dias` : (meses < 12 ? `${meses} meses` : `${(meses/12).toFixed(1)} anos`);

  // ---- A CARTA DA TRAVESSIA (voz do Coração, atravessando o tempo) ----
  const blocos = [];
  blocos.push(`Aqui é o seu Coração quem te escreve — agora olhando para trás. Faz ${periodoTexto} que você caminha comigo, e em ${pontos.length} momentos parou para me escutar. Isso, por si só, já é raro.`);
  blocos.push(`Em todo esse tempo, a estação que mais habitou você foi a do ${morada.nome} — o ${morada.estacao}. É a sua estação-morada: o lugar para onde eu volto quando a vida acalma. Há beleza em ter uma casa interior.`);
  if (despertaram.length) blocos.push(`E houve despertares: ${lista(despertaram.map(d=>'o '+d.nome))} ${despertaram.length>1?'cresceram':'cresceu'} em você ao longo do caminho — ${despertaram.length>1?'estações que':'uma estação que'} antes ${despertaram.length>1?'estavam':'estava'} mais quieta${despertaram.length>1?'s':''} e hoje pulsa${despertaram.length>1?'m':''} com força.`);
  if (silenciaram.length) blocos.push(`Outras se recolheram: ${lista(silenciaram.map(d=>'o '+d.nome))} ${silenciaram.length>1?'pedem':'pede'} atenção renovada — não ${silenciaram.length>1?'sumiram':'sumiu'}, apenas ${silenciaram.length>1?'descansam':'descansa'}, esperando o tempo certo.`);
  if (dominanteMudou) blocos.push(`Você começou esta jornada como ${NOMES[domIni]||domIni} e chega aqui como ${NOMES[domFim]||domFim}. Não é incoerência — é vida. O coração é feito de estações, e cada mudança trouxe uma lição.`);
  else blocos.push(`A sua estação principal se manteve a do ${NOMES[domFim]||domFim} — uma constância que fala de quem você é no fundo, mesmo quando tudo ao redor muda.`);
  blocos.push(`Continue voltando. Cada vez que você me escuta, o mapa fica mais verdadeiro — e você, um pouco mais inteiro.`);
  const carta = {
    saudacao: (opts.nome ? `Querido(a) ${opts.nome},` : 'Ao seu coração, no tempo,'),
    blocos,
    semente: 'O coração é feito de estações — e nenhuma delas é o fim.',
    assinatura: '— o seu Coração, que atravessou tudo isso com você',
  };

  return { suficiente: true, pontos, morada, volatil, despertaram, silenciaram,
    dominanteMudou, dominanteInicial: NOMES[domIni]||domIni, dominanteAtual: NOMES[domFim]||domFim,
    nTestes: pontos.length, periodoTexto, dominantesDistintos: dominantes.length, carta };
}
export default gerarJornada;
